import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { generateUUID } from 'src/app/core/generators/generateUUID';
import { BlackListService } from 'src/app/services/blacklist/black-list.service';
import { SwalAlertService } from 'src/app/services/SwalAlertService/swalAlert.service';
import { Location } from '@angular/common';
import { IdentificadorEnum } from 'src/app/core/enums';
import { IBlackListCreateRequest, IBlackListEditRequest } from 'src/app/services/blacklist/IBlackList';
import { IOptions } from 'src/app/core/types';
import { IDENTIFICADOR_OPTIONS, SITUACAO_OPTIONS } from 'src/app/core/constants/options';

@Component({
  selector: 'app-form-blacklist',
  templateUrl: './form-blacklist.component.html',
  styleUrl: './form-blacklist.component.scss'
})
export class FormBlacklistComponent implements OnInit {
  //Injeções
  fb = inject(FormBuilder);
  router = inject(Router);
  route = inject(ActivatedRoute);
  snackBar = inject(MatSnackBar);
  cd = inject(ChangeDetectorRef);
  swalAlert = inject(SwalAlertService);
  blackListService = inject(BlackListService);

  private subs$ = new Subscription();
  activeRoute: string = '';
  pkCavalo: number = 0;
  isLoading: boolean = false;
  disabledBtn: boolean = false;
  private isPatching = false;
  title: string = 'Cadastrar Restrição';
  guid = generateUUID();

  identificadorOptions: IOptions[] = IDENTIFICADOR_OPTIONS;
  situacaoOptions: IOptions[] = SITUACAO_OPTIONS;

  form = this._initForm();

  location = inject(Location);
  voltar(): void {
    this.location.back();
  }

  private _initForm(): FormGroup<{
    PK_BLCK_LIST: FormControl<number | null>;
    NU_IDNT_CHVE: FormControl<number | null>;
    TX_IDNT_VALR: FormControl<string | null>;
    NU_TIPO_BLOQ: FormControl<number | null>;
    DT_DURC_BLOQ: FormControl<Date | string | null>;
    TX_DTLH_BLOQ: FormControl<string | null>;
  }> {
    return this.fb.group({
      PK_BLCK_LIST: this.fb.control<number | null>(null),

      NU_IDNT_CHVE: this.fb.control<number | null>(null, {
        validators: [Validators.required]
      }),

      TX_IDNT_VALR: this.fb.control<string | null>(null, {
        validators: [Validators.required, Validators.minLength(3)]
      }),

      NU_TIPO_BLOQ: this.fb.control<number | null>(null, {
        validators: [Validators.required]
      }),
      DT_DURC_BLOQ: this.fb.control<Date | string | null>(null),

      TX_DTLH_BLOQ: this.fb.control<string | null>(null, {
        validators: [Validators.required, Validators.minLength(3)]
      })
    });
  }

  // Helpers de rota (modo)
  getParam(param: string): string | null {
    return this.route.snapshot.queryParamMap.get(param);
  }

  get id(): number {
    return Number(this.getParam('PK_BLCK_LIST'));
  }

  get mode(): 'create' | 'edit' | 'view' {
    const modo = (this.getParam('modo') ?? '').toLowerCase().trim();

    if (modo === 'visualizar' || modo === 'view') return 'view';
    if (modo === 'editar' || modo === 'edit') return 'edit';

    return this.id ? 'edit' : 'create';
  }

  get editMode(): boolean {
    return this.mode === 'edit';
  }

  get viewMode(): boolean {
    return this.mode === 'view';
  }

  postarDados(): void {
    this.logInvalidControls(this.form);
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      this.errorMensagem();
      return;
    }
    const raw = this.form.getRawValue();
    const id = Number(raw.NU_IDNT_CHVE);
    const valorRaw = String(raw.TX_IDNT_VALR ?? '').trim();
    const valorTratado =
      id === IdentificadorEnum.CPF || id === IdentificadorEnum.CNPJ
        ? this.onlyDigits(valorRaw)
        : valorRaw;
    const basePayload = {
      NU_IDNT_CHVE: Number(raw.NU_IDNT_CHVE),
      TX_IDNT_VALR: valorTratado,
      NU_TIPO_BLOQ: Number(raw.NU_TIPO_BLOQ),
      DT_DURC_BLOQ: raw.DT_DURC_BLOQ ?? null,
      TX_DTLH_BLOQ: String(raw.TX_DTLH_BLOQ ?? '').trim()
    };

    if (this.editMode) {
      const payload: any = {
        PK_BLCK_LIST: Number(raw.PK_BLCK_LIST ?? this.id),
        ...basePayload
      };
      this.editarForm(payload);
    } else {
      const payload: any = {
        ...basePayload
      };
      this.cadastrarForm(payload);
    }
  }

  cadastrarForm(body: IBlackListCreateRequest): void {
    this.disabledBtn = true;

    this.subs$.add(
      this.blackListService.CreateBlackList(body).subscribe({
        next: () => {
          this.exibirMensagem('Restrição cadastrada com sucesso!', 'success');
          this.disabledBtn = false;
          // volta para lista (ajuste a rota)
          this.router.navigate(['listagem/blacklist']);
        },
        error: () => {
          this.exibirMensagem('Erro ao cadastrar restrição.', 'error');
          this.disabledBtn = false;
        }
      })
    );
  }

  editarForm(body: IBlackListEditRequest): void {
    this.disabledBtn = true;

    this.subs$.add(
      this.blackListService.UpdateBlackList(body).subscribe({
        next: () => {
          this.disabledBtn = false;
          this.router.navigate(['listagem/blacklist']);
        },
        error: () => {
          this.disabledBtn = false;
        }
      })
    );
  }

  exibirMensagem(mensagem: string, type: 'success' | 'error') {
    const config: MatSnackBarConfig = {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: type === 'success' ? ['snackbar-success'] : ['snackbar-error']
    };

    this.snackBar.open(mensagem, 'Fechar', config);
  }

  errorMensagem() {
    this.swalAlert.showAlert(
      'Atenção!',
      'Verifique se todos os campos obrigatórios foram preenchidos corretamente.',
      'warning',
      false,
      false,
      undefined
    );
  }

  public goTo(route: string) {
    this.router.navigate([route]);
    setTimeout(() => {
      this.activeRoute = this.router.url;
      this.cd.detectChanges();
    }, 200);
  }

  ngOnInit(): void {
    this.applyModeUI();
    this.checkPatchForm();
    this.setupIdentificadorMask();
  }

  ngOnDestroy(): void {
    this.subs$.unsubscribe();
  }
  private toDatetimeLocal(
    value: string | Date | null | undefined
  ): string | null {
    if (!value) return null;

    const d = value instanceof Date ? value : new Date(value);

    if (isNaN(d.getTime())) return null;

    const pad = (n: number) => String(n).padStart(2, '0');

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  checkPatchForm(): void {
    const needsId = this.editMode || this.viewMode;

    if (needsId && !this.id) {
      this.swalAlert.showAlert(
        'Atenção!',
        'Não foi possível abrir este registro porque o "identificador" não foi informado.',
        'warning',
        true,
        false,
        { confirmButtonText: 'Voltar' },
        () => this.voltar()
      );
      return;
    }

    if (!needsId) return;

    this.isLoading = true;

    this.subs$.add(
      this.blackListService.GetBlackListById(this.id).subscribe({
        next: res => {
          const apiRes = res?.BlackList;

          if (!apiRes) {
            this.isLoading = false;
            this.swalAlert.showAlert(
              'Atenção!',
              'Registro não encontrado.',
              'warning',
              true,
              false,
              { confirmButtonText: 'Voltar' },
              () => this.voltar()
            );
            return;
          }

          this.isPatching = true;

          this.form.patchValue(
            {
              PK_BLCK_LIST: apiRes.PK_BLCK_LIST,
              NU_IDNT_CHVE: Number(apiRes.NU_IDNT_CHVE),
              TX_IDNT_VALR: apiRes.TX_IDNT_VALR,
              NU_TIPO_BLOQ: Number(apiRes.NU_TIPO_BLOQ),
              DT_DURC_BLOQ:
                this.toDatetimeLocal(apiRes.DT_DURC_BLOQ as any) ?? null,
              TX_DTLH_BLOQ: apiRes.TX_DTLH_BLOQ
            },
            { emitEvent: false }
          );

          this.isPatching = false;

          if (this.viewMode) {
            this.title = 'Visualização Restrição';
            this.form.disable({ emitEvent: false });
          } else {
            this.title = 'Editar Restrição';
            this.form.enable({ emitEvent: false });
          }

          this.isLoading = false;
          setTimeout(() => {
            const id = Number(this.form.controls.NU_IDNT_CHVE.value ?? 0);
            const raw = String(apiRes.TX_IDNT_VALR ?? '');
            const masked = this.applyMaskByIdentificador(
              isNaN(id) ? null : id,
              raw
            );

            this.form.controls.TX_IDNT_VALR.setValue(masked, {
              emitEvent: false
            });

            this.isPatching = false;
            this.cd.detectChanges();
          }, 1000);
        },
        error: (err) => {
          this.isLoading = false;
          if (err?.handled) {
            this.voltar();
            return;
          }
          this.swalAlert.showAlert(
            'Erro!',
            'Não foi possível carregar o registro.',
            'error',
            true,
            false,
            { confirmButtonText: 'Voltar' },
            () => this.voltar()
          );
        }
      })
    );
  }

  escolherSelect(
    value: any,
    controlName: keyof typeof this.form.controls
  ): void {
    if (!this.form.controls[controlName]) {
      return;
    }
    const parsedValue =
      value === '' || value === null || value === undefined
        ? null
        : isNaN(Number(value))
          ? value
          : Number(value);

    this.form.get(controlName)?.setValue(parsedValue);
  }

  private applyModeUI(): void {
    if (this.viewMode) {
      this.title = 'Visualizazão Restrição';
      this.form.disable({ emitEvent: false });
      this.disabledBtn = true;
    } else if (this.editMode) {
      this.title = 'Editar Restrição';
      this.form.enable({ emitEvent: false });
      this.disabledBtn = false;
    } else {
      this.title = 'Cadastrar Restrição';
      this.form.enable({ emitEvent: false });
      this.disabledBtn = false;
    }
  }

  private logInvalidControls(form: FormGroup): void {
    form.markAllAsTouched();

    const invalidControls: Record<string, any> = {};

    Object.keys(form.controls).forEach(controlName => {
      const control = form.get(controlName);

      if (control && control.invalid) {
        invalidControls[controlName] = {
          value: control.value,
          errors: control.errors
        };
      }
    });

    console.group('🚨 Campos inválidos do formulário');
    console.table(invalidControls);
    console.groupEnd();
  }

  private onlyDigits(v: string): string {
    return (v ?? '').replace(/\D+/g, '');
  }

  private maskCPF(v: string): string {
    const d = this.onlyDigits(v).slice(0, 11);
    // 000.000.000-00
    return d
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
  }

  private maskCNPJ(v: string): string {
    const d = this.onlyDigits(v).slice(0, 14);
    // 00.000.000/0000-00
    return d
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
      .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5');
  }

  private maskPlaca(v: string): string {
    const s = (v ?? '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 7);

    // Se bater padrão antigo ABC1234 -> ABC-1234
    if (/^[A-Z]{3}\d{4}$/.test(s)) {
      return s.replace(/^([A-Z]{3})(\d{4})$/, '$1-$2');
    }

    return s;
  }

  private applyMaskByIdentificador(id: number | null, value: string): string {
    switch (id) {
      case IdentificadorEnum.CPF:
        return this.maskCPF(value);

      case IdentificadorEnum.CNPJ:
        return this.maskCNPJ(value);

      case IdentificadorEnum.PlacaCavalo:
      case IdentificadorEnum.PlacaCarreta:
        return this.maskPlaca(value);

      default:
        return value ?? '';
    }
  }
  private setupIdentificadorMask(): void {
    const idCtrl = this.form.controls.NU_IDNT_CHVE;
    const valCtrl = this.form.controls.TX_IDNT_VALR;

    this.subs$.add(
      idCtrl.valueChanges.subscribe(() => {
        if (this.isPatching) return;

        // só limpa quando o usuário realmente troca o tipo
        valCtrl.setValue(null, { emitEvent: false });
        valCtrl.markAsUntouched();
        valCtrl.markAsPristine();
      })
    );

    this.subs$.add(
      valCtrl.valueChanges.subscribe(raw => {
        if (this.isPatching) return;

        const id = Number(idCtrl.value ?? 0);
        const current = String(raw ?? '');

        const masked = this.applyMaskByIdentificador(
          isNaN(id) ? null : id,
          current
        );

        if (masked !== current) {
          valCtrl.setValue(masked, { emitEvent: false });
        }
      })
    );
  }
  get placeholderIdentificador(): string {
    switch (this.form.controls.NU_IDNT_CHVE.value) {
      case IdentificadorEnum.CPF:
        return 'Ex: 123.456.789-00';
      case IdentificadorEnum.CNPJ:
        return 'Ex: 12.345.678/0001-90';
      case IdentificadorEnum.PlacaCavalo:
      case IdentificadorEnum.PlacaCarreta:
        return 'Ex: ABC-1234 ou ABC1D23';
      default:
        return 'Digite aqui';
    }
  }

  onIdentificadorValorChange(v: any): void {
    const id = Number(this.form.controls.NU_IDNT_CHVE.value ?? 0);
    const current = String(v ?? '');

    const masked = this.applyMaskByIdentificador(
      isNaN(id) ? null : id,
      current
    );

    if (masked !== current) {
      this.form.controls.TX_IDNT_VALR.setValue(masked, { emitEvent: false });
      this.cd.detectChanges();
    } else {
      this.form.controls.TX_IDNT_VALR.setValue(current, { emitEvent: false });
    }
  }
  get maskIdentificador(): string {
    switch (Number(this.form.controls.NU_IDNT_CHVE.value)) {
      case IdentificadorEnum.CPF:
        return '000.000.000-00';
      case IdentificadorEnum.CNPJ:
        return '00.000.000/0000-00';
      case IdentificadorEnum.PlacaCavalo:
      case IdentificadorEnum.PlacaCarreta:
        return 'AAA-0A00';
      default:
        return '';
    }
  }
}

